// src/utils/loadHandover.js
//
// When a dispatcher leaves a team without "See and manage" for its loads,
// their unfinished loads on that team still named them as dispatcher. They
// could not work them ("See only" or "No access") and nobody on the team was
// their dispatcher either, so the loads sat with no one running them.
//
// Each such load now goes to whoever took over its driver (driverHandovers),
// else to the driver's own dispatcher on that team, else to nobody: it stays on
// the team, unassigned, where the team's Main Admin sees it in red. The load is
// stamped with its team so it cannot drift with anyone's later moves, and
// records who it came from (dispatcherHandovers), which keeps "See only" on it
// for the person who left (utils/loadAccess).
//
// With "See and manage" nothing is handed over: they finish the load themselves.

import { collection, query, where, getDocs, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { deriveLoadTeamId } from './loadTeam';
import { isOnTeam } from './teams';
import { DEFAULT_CURRENT_STATUSES } from '../components/loads/utils/constants';

/**
 * Read (nothing is written) the loads to hand over. Call before the person's
 * own record changes, so the loads are placed by the teams they are leaving.
 * Returns [{ ref, loadId, teamId, toDispatcherId }].
 */
export async function planLoadHandovers({
  tenantId, leavingUserId, leftTeamIds = [], loadAccess, driverHandovers = [], users = [], drivers = [],
}) {
  if (!tenantId || !leavingUserId || leftTeamIds.length === 0 || loadAccess === 'manage') return [];
  const snap = await getDocs(query(
    collection(db, 'loads'),
    where('tenantId', '==', tenantId),
    where('dispatcherId', '==', leavingUserId),
    where('status', 'in', DEFAULT_CURRENT_STATUSES),
  ));
  const usersById = new Map(users.map(u => [u.id, u]));
  const driversById = new Map(drivers.map(d => [d.id, d]));
  const takenBy = new Map(driverHandovers.map(h => [h.driverId, h.dispatcherId]));

  return snap.docs.flatMap(d => {
    const load = { id: d.id, ...d.data() };
    if (load.isArchived) return [];
    const teamId = deriveLoadTeamId(load, usersById, driversById);
    if (!leftTeamIds.includes(teamId)) return [];
    let toDispatcherId = null;
    if (load.driverId && takenBy.has(load.driverId)) {
      toDispatcherId = takenBy.get(load.driverId);
    } else {
      const driver = driversById.get(load.driverId);
      const own = driver?.dispatcherId;
      if (own && own !== leavingUserId && isOnTeam(usersById.get(own), teamId)) toDispatcherId = own;
    }
    return [{ ref: d.ref, loadId: load.load_id || d.id, teamId, toDispatcherId: toDispatcherId || null }];
  });
}

/** The write for one planned handover. */
export function loadHandoverUpdate(h, leavingUserId, actorId) {
  return {
    teamId: h.teamId,
    dispatcherId: h.toDispatcherId,
    dispatcherHandovers: arrayUnion({
      fromDispatcherId: leavingUserId,
      toDispatcherId: h.toDispatcherId,
      teamId: h.teamId,
      at: new Date().toISOString(),
      by: actorId || null,
    }),
    updatedAt: serverTimestamp(),
  };
}

/**
 * A driver and their dispatcher back together on a new team — moved one after
 * the other, in either order, or linked again afterwards. The loads the
 * dispatcher was running for that driver were left on the old team (handed
 * over with no one to take them, or kept with "See and manage"); they come
 * along to the new team and company and the dispatcher carries on. The old team
 * still sees them read-only, marked as moved, until delivered (teamMoves, as
 * for a driver moved within a company).
 *
 * Only loads still with nobody else: one the dispatcher runs, or one handed
 * over from them that no one has taken. Read only; returns
 * [{ ref, loadId, fromTeamId, fromDispatcherId }].
 */
export async function planRejoinLoads({ tenantId, driver, dispatcherId, users = [], drivers = [] }) {
  const teamId = driver?.teamId || null;
  if (!tenantId || !driver?.id || !dispatcherId || !teamId) return [];
  const usersById = new Map(users.map(u => [u.id, u]));
  if (!isOnTeam(usersById.get(dispatcherId), teamId)) return [];
  const snap = await getDocs(query(
    collection(db, 'loads'),
    where('tenantId', '==', tenantId),
    where('driverId', '==', driver.id),
    where('status', 'in', DEFAULT_CURRENT_STATUSES),
  ));
  const driversById = new Map(drivers.map(d => [d.id, d]));
  driversById.set(driver.id, driver);
  return snap.docs.flatMap(d => {
    const load = { id: d.id, ...d.data() };
    if (load.isArchived) return [];
    const fromTeamId = deriveLoadTeamId(load, usersById, driversById);
    if (!fromTeamId || fromTeamId === teamId) return [];
    const theirs = load.dispatcherId === dispatcherId || (!load.dispatcherId &&
      Array.isArray(load.dispatcherHandovers) &&
      load.dispatcherHandovers.some(h => h?.fromDispatcherId === dispatcherId));
    if (!theirs) return [];
    // Both of them must be on record as having left that team. A driver lent
    // between companies, or a load filed under another team, never moved and
    // must not be carried off (with its company) by a routine re-link.
    const left = (person) => (Array.isArray(person?.formerTeams) ? person.formerTeams : [])
      .some(f => f?.teamId === fromTeamId);
    if (!left(driver) || !left(usersById.get(dispatcherId))) return [];
    return [{ ref: d.ref, loadId: load.load_id || d.id, fromTeamId, fromDispatcherId: load.dispatcherId || null }];
  });
}

/** The write for one load rejoining its driver and dispatcher. */
export function rejoinUpdate(r, { driver, dispatcherId, actorId }) {
  return {
    teamId: driver.teamId,
    parentCompanyId: driver.parentCompanyId || null,
    parentCompanyName: driver.parentCompanyName || null,
    dispatcherId,
    teamMoves: arrayUnion({
      fromTeamId: r.fromTeamId,
      toTeamId: driver.teamId,
      fromDispatcherId: r.fromDispatcherId,
      movedAt: new Date().toISOString(),
      movedBy: actorId || null,
    }),
    updatedAt: serverTimestamp(),
  };
}
